import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core'

type DemoKind = 'follow' | 'teamVideos' | 'matchPitch' | 'drawSteps' | 'lineup' | 'compareSeasons' | 'formation'

/** Built-in demos, by announcement slug. A real clip (video_url) always wins over these. */
const DEMOS: Record<string, DemoKind> = {
  'follow-a-player': 'follow',
  'team-videos': 'teamVideos',
  'match-videos-pitch': 'matchPitch',
  'match-videos-pitch-player': 'matchPitch',
  'drawing-steps': 'drawSteps',
  'most-used-line-up': 'lineup',
  'compare-season-v-season': 'compareSeasons',
  'compare-me-last-season': 'compareSeasons',
  'formation-suggestion': 'formation',
}

/** Whether a slug has a built-in demo. */
export function hasWhatsNewDemo (slug: string | null | undefined): boolean {
  return !!slug && slug in DEMOS
}

/**
 * A short looping animation of a feature, drawn in code (What's new, CC 19/10/2026), so a card has
 * something moving before the real demo video exists. 12 s loop, silent, captions burned in;
 * `compact` drops the captions for the bell's thumbnails. Still under prefers-reduced-motion.
 * Pitch, colours and timing are shared, so every feature's demo looks like the same family.
 */
@Component({
  selector: 'app-whats-new-demo',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (kind(); as k) {
      <div class="demo" [class.demo--compact]="compact()" role="img" [attr.aria-label]="compact() ? null : label()">
        @if (k !== 'compareSeasons') {
          <span class="pitch"></span><span class="half"></span><span class="circle"></span>
        }
        @switch (k) {
          @case ('follow') {
            <span class="dot dot--a"></span><span class="dot dot--b"></span><span class="dot dot--c"></span><span class="dot dot--d"></span>
            <span class="tap"></span>
            <span class="me"><span class="ring"></span></span>
          }
          @case ('teamVideos') {
            <span class="chips">
              <span class="chip">Build-up</span><span class="chip chip--on">Counter-press</span><span class="chip">Set pieces</span>
            </span>
            <svg class="lines" viewBox="0 0 160 90" preserveAspectRatio="none" aria-hidden="true">
              <path class="run run--1" d="M30 70 L58 52 L86 58 L118 30" />
              <path class="run run--2" d="M36 30 L70 40 L104 22 L132 44" />
            </svg>
            <span class="badge badge--count">12 clips · back to back</span>
          }
          @case ('matchPitch') {
            <span class="toggle"><span class="toggle__on">Pitch</span><span>Cards</span></span>
            <span class="mark mark--1"></span><span class="mark mark--2"></span><span class="mark mark--3"></span>
            <span class="mark mark--4 mark--away"></span><span class="mark mark--5 mark--away"></span>
            <span class="row">Shots · 7 <span class="row__play">▶</span></span>
          }
          @case ('drawSteps') {
            <svg class="lines" viewBox="0 0 160 90" preserveAspectRatio="none" aria-hidden="true">
              <path class="arrow arrow--1" d="M34 64 L74 44" />
              <path class="arrow arrow--2" d="M74 44 L120 50" />
              <path class="arrow arrow--3" d="M120 50 L136 26" />
            </svg>
            <span class="pip pip--1">1</span><span class="pip pip--2">2</span><span class="pip pip--3">3</span>
          }
          @case ('lineup') {
            <span class="xi xi--gk" style="left:8%;top:50%"></span>
            <span class="xi" style="left:24%;top:20%"></span><span class="xi" style="left:24%;top:40%"></span>
            <span class="xi" style="left:24%;top:60%"></span><span class="xi" style="left:24%;top:80%"></span>
            <span class="xi" style="left:46%;top:30%"></span><span class="xi" style="left:46%;top:50%"></span>
            <span class="xi" style="left:46%;top:70%"></span>
            <span class="xi" style="left:70%;top:22%"></span><span class="xi" style="left:72%;top:50%"></span>
            <span class="xi" style="left:70%;top:78%"></span>
            <span class="badge badge--shape">4-3-3 · 9 of 12 matches</span>
          }
          @case ('compareSeasons') {
            <span class="bars">
              @for (b of bars; track $index) {
                <span class="bar-pair">
                  <span class="b b--old" [style.--h]="b[0] + '%'"></span>
                  <span class="b b--new" [style.--h]="b[1] + '%'"></span>
                  <span class="b__lbl">{{ b[2] }}</span>
                </span>
              }
            </span>
            <span class="legend"><span class="lg lg--old">25/26</span><span class="lg lg--new">26/27</span></span>
          }
          @case ('formation') {
            @for (p of shape; track $index) {
              <span class="fm" [style.--x0]="p[0] + '%'" [style.--y0]="p[1] + '%'" [style.--x1]="p[2] + '%'" [style.--y1]="p[3] + '%'"></span>
            }
            <span class="badge badge--shape badge--late">Suggested: 4-2-3-1</span>
          }
        }
        @if (!compact()) {
          <span class="cap cap--1">{{ caps()[0] }}</span>
          <span class="cap cap--2">{{ caps()[1] }}</span>
          <span class="cap cap--3">{{ caps()[2] }}</span>
          <span class="len">0:12 · no sound</span>
        }
        <span class="prog"></span>
      </div>
    }
  `,
  styles: [`
    :host { display: block; }
    .demo { position: relative; width: 100%; aspect-ratio: 16 / 9; border-radius: 10px; overflow: hidden; background: #0E2A24; font-family: var(--font-family-primary, sans-serif); }
    .demo--compact { border-radius: 6px; height: 100%; aspect-ratio: auto; }
    .pitch { position: absolute; inset: 6%; border: 1px solid rgba(232, 238, 255, .18); border-radius: 3px; }
    .half { position: absolute; left: 50%; top: 6%; bottom: 6%; border-left: 1px solid rgba(232, 238, 255, .18); }
    .circle { position: absolute; left: 50%; top: 50%; width: 18%; aspect-ratio: 1; transform: translate(-50%, -50%); border: 1px solid rgba(232, 238, 255, .18); border-radius: 50%; }

    /* Captions and progress: shared by every demo. */
    .cap { position: absolute; left: 0; right: 0; bottom: 8%; text-align: center; font-size: 13px; font-weight: 600; color: #E8EEFF; text-shadow: 0 1px 3px rgba(0, 0, 0, .8); opacity: 0; animation: 12s infinite; }
    .cap--1 { animation-name: wn-c1; } .cap--2 { animation-name: wn-c2; } .cap--3 { animation-name: wn-c3; }
    .len { position: absolute; top: 6px; right: 6px; padding: 2px 6px; border-radius: 6px; background: rgba(8, 13, 26, .75); font-size: 12px; color: #C0CCDD; }
    .prog { position: absolute; left: 0; bottom: 0; height: 3px; background: #06D6F5; animation: wn-bar 12s linear infinite; }
    .demo--compact .prog { height: 2px; }
    .badge { position: absolute; left: 50%; top: 9%; transform: translateX(-50%); padding: 3px 8px; border-radius: 6px; background: rgba(8, 13, 26, .8); color: #E8EEFF; font-size: 12px; font-weight: 600; white-space: nowrap; opacity: 0; animation: wn-late 12s infinite; }
    .demo--compact .badge, .demo--compact .chips, .demo--compact .toggle, .demo--compact .row, .demo--compact .legend, .demo--compact .b__lbl { display: none; }

    /* Follow a player. */
    .dot, .me { position: absolute; width: 5.5%; aspect-ratio: 1; border-radius: 50%; transform: translate(-50%, -50%); }
    .dot { animation: 12s ease-in-out infinite alternate; }
    .dot--a { background: #06D6F5; animation-name: wn-da; }
    .dot--b { background: #FF2882; animation-name: wn-db; }
    .dot--c { background: #06D6F5; animation-name: wn-dc; }
    .dot--d { background: #FF2882; animation-name: wn-dd; }
    .me { width: 6.5%; background: #E8EEFF; animation: wn-me 12s ease-in-out infinite; }
    .ring { position: absolute; left: 50%; top: 50%; width: 260%; aspect-ratio: 1; border: 2px solid #06D6F5; border-radius: 50%; transform: translate(-50%, -50%); animation: wn-ring 12s ease-out infinite; }
    .tap { position: absolute; left: 22%; top: 62%; width: 18%; aspect-ratio: 1; border-radius: 50%; background: rgba(232, 238, 255, .35); transform: translate(-50%, -50%); animation: wn-tap 12s ease-out infinite; }

    /* Lines drawn along a path (Team Videos runs, Drawing arrows). */
    .lines { position: absolute; inset: 0; width: 100%; height: 100%; }
    .run, .arrow { fill: none; stroke-linecap: round; stroke-linejoin: round; stroke-dasharray: 200; stroke-dashoffset: 200; animation: 12s ease-out infinite; }
    .run { stroke: #06D6F5; stroke-width: 1.6; }
    .run--1 { animation-name: wn-draw1; } .run--2 { stroke: #FF2882; animation-name: wn-draw2; }
    .arrow { stroke: #FFD24A; stroke-width: 2.2; }
    .arrow--1 { animation-name: wn-draw1; } .arrow--2 { animation-name: wn-draw2; } .arrow--3 { animation-name: wn-draw3; }
    .chips { position: absolute; left: 6%; top: 9%; display: flex; gap: 4px; }
    .chip { padding: 2px 7px; border-radius: 999px; border: 1px solid rgba(232, 238, 255, .3); color: #C0CCDD; font-size: 12px; background: rgba(8, 13, 26, .6); }
    .chip--on { border-color: #06D6F5; color: #E8EEFF; background: rgba(6, 214, 245, .2); animation: wn-pick 12s infinite; }
    .badge--count { top: auto; bottom: 22%; }
    .pip { position: absolute; width: 16px; height: 16px; margin: -8px 0 0 -8px; border-radius: 50%; background: #FFD24A; color: #080D1A; font-size: 12px; font-weight: 700; line-height: 16px; text-align: center; opacity: 0; animation: 12s infinite; }
    .pip--1 { left: 34%; top: 64%; animation-name: wn-pip1; } .pip--2 { left: 46%; top: 49%; animation-name: wn-pip2; } .pip--3 { left: 75%; top: 56%; animation-name: wn-pip3; }

    /* Match Videos: pitch view. */
    .toggle { position: absolute; left: 6%; top: 9%; display: flex; border: 1px solid rgba(232, 238, 255, .3); border-radius: 6px; overflow: hidden; font-size: 12px; color: #C0CCDD; background: rgba(8, 13, 26, .6); }
    .toggle span { padding: 2px 8px; }
    .toggle__on { background: rgba(6, 214, 245, .25); color: #E8EEFF; }
    .mark { position: absolute; width: 4.5%; aspect-ratio: 1; border-radius: 50%; background: #06D6F5; transform: translate(-50%, -50%) scale(0); animation: 12s infinite; }
    .mark--away { background: #FF2882; }
    .mark--1 { left: 78%; top: 40%; animation-name: wn-pop1; } .mark--2 { left: 84%; top: 56%; animation-name: wn-pop2; } .mark--3 { left: 72%; top: 62%; animation-name: wn-pop3; }
    .mark--4 { left: 20%; top: 46%; animation-name: wn-pop2; } .mark--5 { left: 26%; top: 34%; animation-name: wn-pop3; }
    .row { position: absolute; right: 6%; top: 9%; padding: 2px 8px; border-radius: 6px; background: rgba(8, 13, 26, .75); color: #E8EEFF; font-size: 12px; font-weight: 600; opacity: 0; animation: wn-late 12s infinite; }
    .row__play { color: #06D6F5; margin-left: 4px; }

    /* Most used line-up. */
    .xi { position: absolute; width: 5%; aspect-ratio: 1; border-radius: 50%; background: #06D6F5; transform: translate(-50%, -50%) scale(0); animation: wn-xi 12s infinite; }
    .xi--gk { background: #FFD24A; }
    .xi:nth-child(5) { animation-delay: .2s; } .xi:nth-child(6) { animation-delay: .35s; } .xi:nth-child(7) { animation-delay: .5s; }
    .xi:nth-child(8) { animation-delay: .65s; } .xi:nth-child(9) { animation-delay: .8s; } .xi:nth-child(10) { animation-delay: .95s; }
    .xi:nth-child(11) { animation-delay: 1.1s; } .xi:nth-child(12) { animation-delay: 1.25s; } .xi:nth-child(13) { animation-delay: 1.4s; }
    .xi:nth-child(14) { animation-delay: 1.55s; }
    .badge--shape { top: auto; bottom: 22%; }

    /* Compare: season v season. */
    .bars { position: absolute; left: 10%; right: 10%; top: 14%; bottom: 30%; display: flex; align-items: flex-end; justify-content: space-around; }
    .bar-pair { position: relative; display: flex; align-items: flex-end; gap: 3px; height: 100%; }
    .b { width: 12px; height: var(--h); border-radius: 3px 3px 0 0; transform-origin: bottom; transform: scaleY(0); animation: wn-grow 12s ease-out infinite; }
    .b--old { background: rgba(232, 238, 255, .45); }
    .b--new { background: #06D6F5; animation-name: wn-grow2; }
    .b__lbl { position: absolute; left: 50%; bottom: -16px; transform: translateX(-50%); font-size: 12px; color: #C0CCDD; white-space: nowrap; }
    .legend { position: absolute; left: 6%; top: 8%; display: flex; gap: 8px; font-size: 12px; color: #C0CCDD; }
    .lg::before { content: ''; display: inline-block; width: 8px; height: 8px; border-radius: 2px; margin-right: 4px; vertical-align: -1px; }
    .lg--old::before { background: rgba(232, 238, 255, .45); } .lg--new::before { background: #06D6F5; }

    /* Formation suggestion: from where they stood to the shape. */
    .fm { position: absolute; width: 4.5%; aspect-ratio: 1; border-radius: 50%; background: #06D6F5; transform: translate(-50%, -50%); left: var(--x0); top: var(--y0); animation: wn-shape 12s ease-in-out infinite; }
    .badge--late { top: auto; bottom: 22%; }

    @keyframes wn-me { 0%, 30% { left: 22%; top: 62%; } 55% { left: 44%; top: 46%; } 80% { left: 63%; top: 30%; } 100% { left: 70%; top: 26%; } }
    @keyframes wn-ring { 0%, 30% { opacity: 0; transform: translate(-50%, -50%) scale(1.8); } 36%, 100% { opacity: 1; transform: translate(-50%, -50%) scale(1); } }
    @keyframes wn-tap { 0%, 24% { opacity: 0; transform: translate(-50%, -50%) scale(.4); } 28% { opacity: .9; transform: translate(-50%, -50%) scale(1); } 34%, 100% { opacity: 0; transform: translate(-50%, -50%) scale(1.6); } }
    @keyframes wn-da { from { left: 40%; top: 55%; } to { left: 52%; top: 40%; } }
    @keyframes wn-db { from { left: 58%; top: 70%; } to { left: 66%; top: 52%; } }
    @keyframes wn-dc { from { left: 30%; top: 30%; } to { left: 40%; top: 24%; } }
    @keyframes wn-dd { from { left: 75%; top: 45%; } to { left: 78%; top: 34%; } }
    @keyframes wn-c1 { 0%, 30% { opacity: 1; } 33%, 100% { opacity: 0; } }
    @keyframes wn-c2 { 0%, 30% { opacity: 0; } 33%, 63% { opacity: 1; } 66%, 100% { opacity: 0; } }
    @keyframes wn-c3 { 0%, 63% { opacity: 0; } 66%, 97% { opacity: 1; } 100% { opacity: 0; } }
    @keyframes wn-bar { from { width: 0; } to { width: 100%; } }
    @keyframes wn-late { 0%, 62% { opacity: 0; } 68%, 96% { opacity: 1; } 100% { opacity: 0; } }
    @keyframes wn-pick { 0%, 22% { background: rgba(8, 13, 26, .6); border-color: rgba(232, 238, 255, .3); } 28%, 100% { background: rgba(6, 214, 245, .2); border-color: #06D6F5; } }
    @keyframes wn-draw1 { 0%, 30% { stroke-dashoffset: 200; } 50%, 96% { stroke-dashoffset: 0; } 100% { stroke-dashoffset: 200; } }
    @keyframes wn-draw2 { 0%, 50% { stroke-dashoffset: 200; } 66%, 96% { stroke-dashoffset: 0; } 100% { stroke-dashoffset: 200; } }
    @keyframes wn-draw3 { 0%, 66% { stroke-dashoffset: 200; } 80%, 96% { stroke-dashoffset: 0; } 100% { stroke-dashoffset: 200; } }
    @keyframes wn-pip1 { 0%, 30% { opacity: 0; } 34%, 96% { opacity: 1; } 100% { opacity: 0; } }
    @keyframes wn-pip2 { 0%, 50% { opacity: 0; } 54%, 96% { opacity: 1; } 100% { opacity: 0; } }
    @keyframes wn-pip3 { 0%, 66% { opacity: 0; } 70%, 96% { opacity: 1; } 100% { opacity: 0; } }
    @keyframes wn-pop1 { 0%, 34% { transform: translate(-50%, -50%) scale(0); } 38%, 96% { transform: translate(-50%, -50%) scale(1); } 100% { transform: translate(-50%, -50%) scale(0); } }
    @keyframes wn-pop2 { 0%, 40% { transform: translate(-50%, -50%) scale(0); } 44%, 96% { transform: translate(-50%, -50%) scale(1); } 100% { transform: translate(-50%, -50%) scale(0); } }
    @keyframes wn-pop3 { 0%, 46% { transform: translate(-50%, -50%) scale(0); } 50%, 96% { transform: translate(-50%, -50%) scale(1); } 100% { transform: translate(-50%, -50%) scale(0); } }
    @keyframes wn-xi { 0%, 10% { transform: translate(-50%, -50%) scale(0); } 16%, 96% { transform: translate(-50%, -50%) scale(1); } 100% { transform: translate(-50%, -50%) scale(0); } }
    @keyframes wn-grow { 0%, 12% { transform: scaleY(0); } 30%, 96% { transform: scaleY(1); } 100% { transform: scaleY(0); } }
    @keyframes wn-grow2 { 0%, 40% { transform: scaleY(0); } 58%, 96% { transform: scaleY(1); } 100% { transform: scaleY(0); } }
    @keyframes wn-shape { 0%, 30% { left: var(--x0); top: var(--y0); } 60%, 96% { left: var(--x1); top: var(--y1); } 100% { left: var(--x0); top: var(--y0); } }

    /* Still picture for people who turn motion off: the finished frame, last caption shown. */
    @media (prefers-reduced-motion: reduce) {
      .demo * { animation: none !important; }
      .me { left: 63%; top: 30%; }
      .tap, .cap--1, .cap--2 { opacity: 0; }
      .cap--3, .badge, .row, .pip { opacity: 1; }
      .prog { width: 100%; }
      .run, .arrow { stroke-dashoffset: 0; }
      .mark, .xi { transform: translate(-50%, -50%) scale(1); }
      .b { transform: none; }
      .fm { left: var(--x1); top: var(--y1); }
    }
  `],
})
export class WhatsNewDemoComponent {
  readonly slug = input<string | null>(null)
  readonly compact = input(false)
  readonly kind = computed<DemoKind | null>(() => DEMOS[this.slug() ?? ''] ?? null)

  /** Compare demo: [last season %, this season %, label]. */
  protected readonly bars: [number, number, string][] = [[55, 80, 'Goals'], [70, 62, 'Duels'], [40, 75, 'Key passes'], [60, 70, 'xG']]
  /** Formation demo: [x from, y from, x to, y to] in % — scattered positions settling into 4-2-3-1. */
  protected readonly shape: [number, number, number, number][] = [
    [12, 46, 9, 50],
    [30, 18, 25, 18], [22, 38, 22, 40], [26, 64, 22, 60], [32, 84, 25, 82],
    [40, 36, 42, 38], [46, 60, 42, 62],
    [58, 22, 62, 22], [54, 50, 60, 50], [62, 76, 62, 78],
    [70, 46, 80, 50],
  ]

  private static readonly CAPS: Record<DemoKind, [string, string, string]> = {
    follow: ['1 · Tap Follow', '2 · Tap the player', '3 · The ring stays on them'],
    teamVideos: ['1 · Open a team\'s Videos', '2 · Pick a phase of play', '3 · Every clip, back to back'],
    matchPitch: ['1 · Pitch view', '2 · See where it happened', '3 · Tap a row to play it'],
    drawSteps: ['1 · Draw an arrow', '2 · Add the next step', '3 · They play one after another'],
    lineup: ['1 · Open your team', '2 · Most used line-up', '3 · How often it started'],
    compareSeasons: ['1 · Pick a player', '2 · Season v season', '3 · See what changed'],
    formation: ['1 · Where they really stood', '2 · Moves into shape', '3 · The suggested formation'],
  }
  private static readonly LABELS: Record<DemoKind, string> = {
    follow: 'Demo: tap Follow, tap the player, the ring stays on them',
    teamVideos: 'Demo: pick a phase of play and its clips play back to back',
    matchPitch: 'Demo: the match pitch view marks where things happened',
    drawSteps: 'Demo: draw arrows as steps that play one after another',
    lineup: 'Demo: the team\'s most used starting line-up appears on the pitch',
    compareSeasons: 'Demo: last season and this season side by side',
    formation: 'Demo: players settle from where they stood into the suggested formation',
  }

  readonly caps = computed(() => WhatsNewDemoComponent.CAPS[this.kind() ?? 'follow'])
  readonly label = computed(() => WhatsNewDemoComponent.LABELS[this.kind() ?? 'follow'])
}
